/**
 * Account picker: single-select for leader/co-leader.
 * Uses searchAccounts (min 2 chars) to find accounts.
 */

import { useState, useMemo } from "react";
import {
  IconChevronDown,
  IconSearch,
  IconCheck,
  IconX,
} from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";
import { useDebouncedValue } from "../../../../lib/use-debounce.ts";
import type { AdminAccountRef } from "../types.ts";
import { formatAccountName } from "../types.ts";
import { useSearchAccounts, useAdminAccounts } from "../../shared/hooks";

interface AccountPickerProps {
  value: AdminAccountRef | null;
  onChange: (account: AdminAccountRef | null) => void;
  placeholder?: string;
  /** Optional: exclude this account id from selection (e.g. current leader when picking co-leader) */
  excludeId?: string | null;
  /** Optional: when set, trigger renders as settings row (label left, value right, chevron) */
  label?: string;
}

export function AccountPicker({
  value,
  onChange,
  placeholder = "Выберите участника...",
  excludeId,
  label,
}: AccountPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const shouldSearch = debouncedSearch.length >= 2;

  // URQL hooks - use search if 2+ chars, else use admin accounts list
  const {
    accounts: searchResults,
    isLoading: isSearching,
    error: searchError,
  } = useSearchAccounts({ query: debouncedSearch, pause: !isOpen || !shouldSearch });

  const {
    accounts: adminAccounts,
    isLoading: isLoadingAdmins,
    error: adminsError,
  } = useAdminAccounts({ pause: !isOpen || shouldSearch });

  // Determine which accounts to show
  const accounts = shouldSearch ? searchResults : adminAccounts;
  const isLoading = shouldSearch ? isSearching : isLoadingAdmins;
  const error = (shouldSearch ? searchError : adminsError)?.message ?? null;

  const filtered = useMemo(() => {
    return excludeId ? accounts.filter((a) => a.id !== excludeId) : accounts;
  }, [accounts, excludeId]);

  const selectAccount = (account: AdminAccountRef) => {
    haptic("light");
    onChange(account);
    setIsOpen(false);
  };

  const clearSelection = () => {
    haptic("light");
    onChange(null);
    if (value) setIsOpen(false);
  };

  const triggerContent = label ? (
    <>
      <span className="text-[15px] text-slate-500 w-28 flex-shrink-0">
        {label}
      </span>
      <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
        <span
          className={`text-[15px] truncate ${
            isOpen ? "text-[var(--color-accent)] font-medium" : "text-slate-900"
          }`}
        >
          {value ? formatAccountName(value) : placeholder}
        </span>
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearSelection();
            }}
            className="p-1 rounded-full hover:bg-slate-100 active:bg-slate-200 flex-shrink-0"
            aria-label="Очистить"
          >
            <IconX className="w-4 h-4 text-slate-400" />
          </button>
        )}
        <IconChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform ${
            isOpen ? "rotate-180 text-[var(--color-accent)]" : "text-slate-400"
          }`}
        />
      </div>
    </>
  ) : (
    <>
      <span className="text-[15px] text-slate-900 truncate">
        {value ? formatAccountName(value) : placeholder}
      </span>
      <div className="flex items-center gap-1 flex-shrink-0">
        {value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearSelection();
            }}
            className="p-1 rounded-full hover:bg-slate-100 active:bg-slate-200"
            aria-label="Очистить"
          >
            <IconX className="w-4 h-4 text-slate-400" />
          </button>
        )}
        <IconChevronDown
          className={`w-5 h-5 text-slate-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>
    </>
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          haptic("light");
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center text-left transition-colors ${
          label
            ? `min-h-[52px] px-4 border-b border-slate-100 last:border-b-0 active:bg-slate-50 ${isOpen ? "bg-slate-50" : ""}`
            : "gap-2 px-3 py-2.5 border border-slate-200 rounded-xl bg-white active:bg-slate-50"
        }`}
      >
        {triggerContent}
      </button>

      {isOpen && (
        <>
          {!label && (
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
          )}
          <div
            className={
              label
                ? "mx-3 mb-2 rounded-xl overflow-hidden bg-white border border-slate-200 shadow-sm"
                : "absolute top-full left-0 right-0 mt-1 z-20 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden"
            }
          >
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Поиск по имени или username..."
                  className="w-full pl-9 pr-3 py-2 text-[14px] bg-slate-50 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {debouncedSearch.length > 0 && debouncedSearch.length < 2 && (
                <p className="text-[11px] text-slate-400 mt-1">
                  Введите минимум 2 символа
                </p>
              )}
            </div>
            <div className="max-h-[250px] overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  Загрузка...
                </div>
              ) : error ? (
                <div className="p-4 text-center text-red-500 text-sm">
                  {error}
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  {debouncedSearch.length >= 2
                    ? "Ничего не найдено"
                    : "Введите имя для поиска"}
                </div>
              ) : (
                filtered.map((account) => {
                  const isSelected = value?.id === account.id;
                  return (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => selectAccount(account)}
                      className={`w-full px-3 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        isSelected ? "bg-slate-50" : "active:bg-slate-50"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? "bg-[var(--color-accent)] border-[var(--color-accent)]"
                            : "border-slate-300"
                        }`}
                      >
                        {isSelected && (
                          <IconCheck className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[15px] text-slate-900 block truncate">
                          {formatAccountName(account)}
                        </span>
                        {account.username && (
                          <span className="text-[12px] text-slate-500 block truncate">
                            @{account.username}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
